import React from 'react';
import { List } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import {
    onReactSelectChanged, toasty,
} from '../../common/forms/FormElements';
import { SaveButton } from '../../common/forms/SaveButton';
import ValidatedSelect from '../../common/forms/ValidatedSelect';
import { BlockBorder } from '../../common/Block';

/**
 * Shared class component for removing charges from a timesheet.
 * Can be used by both Admin Tools and Support Tools pages.
 *
 * Props:
 * @param {function} onSuccess - Callback after successful submission (e.g., refreshUsageHistory)
 */
class RemoveChargesForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            timesheetId: '',
            timesheetDetailIds: [],
            selectedTimesheetOption: null,
            chargesList: [],
        };
        this.onReactSelectChanged = onReactSelectChanged.bind(this);
    }

    onTimesheetChanged = async (selectedOption) => {
        this.setState({
            selectedTimesheetOption: selectedOption,
            timesheetId: selectedOption?.value ?? '',
            timesheetDetailIds: [],
            chargesList: [],
        });

        if (selectedOption?.value) {
            await this.loadChargesList(selectedOption.value);
        }
    };

    loadChargesList = async (timesheetId) => {
        try {
            const timesheetDetails = await httpClient.js(
                ApiRoutes.typeAheads.timesheetDetails(timesheetId),
            );

            if (!timesheetDetails || timesheetDetails.length === 0) {
                this.setState({ chargesList: [] });
                return;
            }

            const options = timesheetDetails.map((item) => ({
                value: item.value,
                label: `[${item.label}] ${item.employee ? ` - ${item.employee}` : ''}`,
            }));

            this.setState({ chargesList: options });
        } catch (err) {
            console.error(err);
            this.setState({ chargesList: [] });
        }
    };

    onChargesChanged = (selectedOptions) => {
        this.setState({
            timesheetDetailIds: (selectedOptions ?? []).map((o) => o.value),
        });
    };

    loadTimesheetOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.timesheetNumberSearch(inputValue))
            .then((data) => callback(data));
    }, 250);

    onSubmit = async (e) => {
        if (e) e.preventDefault();

        const { timesheetId, timesheetDetailIds } = this.state;

        if (!timesheetId) {
            toasty.error('Please select a timesheet.');
            return;
        }

        if (!timesheetDetailIds || timesheetDetailIds.length === 0) {
            toasty.error('Please select at least one charge type.');
            return;
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.adminTool.removeCharges(), {
                timesheetId,
                timesheetDetailIds,
            });

            this.setState({
                timesheetId: '',
                timesheetDetailIds: [],
                selectedTimesheetOption: null,
                chargesList: [],
            });

            toasty.success('Remove Charges from Timesheet Succeeded');

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } catch (err) {
            console.error(err);
            toasty.error('Error: Removing Charges from Timesheet Failed. Please Check Form Inputs');
            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const {
            saving, selectedTimesheetOption, chargesList, timesheetDetailIds,
        } = this.state;
        const isSubmitDisabled = saving || !selectedTimesheetOption || !timesheetDetailIds.length;

        return (
            <form onSubmit={this.onSubmit}>
                <ValidatedSelect
                    component={AsyncSelect}
                    loadOptions={this.loadTimesheetOptions}
                    placeholder="Type Timesheet Number Here"
                    id="timesheetNumber"
                    label="Timesheet Number"
                    value={selectedTimesheetOption}
                    onChange={this.onTimesheetChanged}
                    cacheOptions
                    required
                    error="Timesheet Number is required."
                />

                <ValidatedSelect
                    id="baseChargeType"
                    name="timesheetDetailIds"
                    label="Charges to be Removed"
                    required
                    options={chargesList}
                    value={(chargesList ?? []).filter((x) => (timesheetDetailIds ?? []).includes(x.value))}
                    onChange={this.onChargesChanged}
                    error="At least one charge type selection is required."
                    isMulti
                    isDisabled={!selectedTimesheetOption}
                />

                <BlockBorder />

                <div className="d-flex justify-content-center">
                    <SaveButton
                        saving={saving}
                        disabled={isSubmitDisabled}
                        label="Submit"
                    />
                </div>
            </form>
        );
    }
}

/**
 * Renders the execution history details for RemoveCharges.
 * Can be used by both Admin and SupportTools ExecutionHistory components.
 */
export const RemoveChargesHistoryDetails = ({ record }) => (
    <List type="unstyled">
        {record.input.timesheetNumber && (
            <li>
                <strong>Timesheet:</strong>
                {' '}
                {record.input.timesheetNumber}
            </li>
        )}
        {record.input.removedCharges && record.input.removedCharges.length > 0 && (
            <li>
                <strong>Charges Removed:</strong>
                <ul>
                    {record.input.removedCharges.map((charge, index) => (

                        <li key={index}>{charge}</li>
                    ))}
                </ul>
            </li>
        )}
        {record.input.chargeWithIssue && (
            <li>
                <strong>Charges With Issue:</strong>
                {' '}
                {record.input.chargeWithIssue}
            </li>
        )}
    </List>
);

export { RemoveChargesForm };
export default RemoveChargesForm;
